(() => {
  'use strict';

  const elementName = 'kiekkokingi-brand-directory';
  if (customElements.get(elementName)) return;

  class KiekkoKingiBrandDirectory extends HTMLElement {
    connectedCallback() {
      if (this.isDirectoryConnected) return;
      this.isDirectoryConnected = true;
      this.searchShell = this.querySelector('[data-search-shell]');
      this.searchInput = this.querySelector('[data-brand-search]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.countElement = this.querySelector('[data-brand-count]');
      this.noResults = this.querySelector('[data-no-results]');
      this.items = Array.from(this.querySelectorAll('[data-brand-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-brand-group][data-group-key]'));
      this.groupLinks = Array.from(this.querySelectorAll('[data-group-target]'));
      this.onSearch = this.filter.bind(this);
      this.onClear = this.clear.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onHashChange = this.syncActiveGroupLinkFromHash.bind(this);

      try {
        this.collator = new Intl.Collator('fi', { sensitivity: 'base', numeric: true });
      } catch {
        this.collator = null;
      }

      this.sortGroups();

      if (this.searchShell && this.searchInput) {
        this.searchShell.hidden = false;
        this.searchInput.addEventListener('input', this.onSearch);
        this.searchInput.addEventListener('search', this.onSearch);
      }
      if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
      window.addEventListener('hashchange', this.onHashChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.filter();
      this.syncActiveGroupLinkFromHash();
    }

    disconnectedCallback() {
      this.isDirectoryConnected = false;
      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this.onSearch);
        this.searchInput.removeEventListener('search', this.onSearch);
      }
      if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
      window.removeEventListener('hashchange', this.onHashChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fi')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    compare(first, second) {
      const firstValue = first.dataset.brandSortKey || '';
      const secondValue = second.dataset.brandSortKey || '';
      if (this.collator) return this.collator.compare(firstValue, secondValue);
      return firstValue.localeCompare(secondValue, 'fi', { sensitivity: 'base', numeric: true });
    }

    sortGroups() {
      this.groups.forEach((group) => {
        const list = group.querySelector('[data-brand-list]');
        if (!list) return;
        const items = Array.from(list.querySelectorAll('[data-brand-item]'));
        items.sort((first, second) => this.compare(first, second));
        items.forEach((item) => list.append(item));
      });
    }

    filter() {
      const query = this.normalize(this.searchInput ? this.searchInput.value : '');
      let visibleCount = 0;

      this.items.forEach((item) => {
        const isMatch = !query || this.normalize(item.dataset.brandSearch).includes(query);
        item.hidden = !isMatch;
        if (isMatch) visibleCount += 1;
      });

      this.groups.forEach((group) => {
        const hasVisibleBrands = Array.from(group.querySelectorAll('[data-brand-item]')).some(
          (item) => !item.hidden,
        );
        group.hidden = !hasVisibleBrands;
        const groupKey = group.dataset.groupKey;
        this.groupLinks
          .filter((link) => link.dataset.groupTarget === groupKey)
          .forEach((link) => {
            const alphabetItem = link.closest('[data-alphabet-item]');
            if (alphabetItem) alphabetItem.hidden = !hasVisibleBrands;
          });
      });

      if (this.clearButton) this.clearButton.hidden = !query;
      if (this.noResults) this.noResults.hidden = visibleCount !== 0;
      if (this.countElement) {
        const pluralUnit = (this.dataset.countUnitPlural || '').trim();
        const singularUnit = (this.dataset.countUnitSingular || '').trim();
        const unit = visibleCount === 1 && singularUnit ? singularUnit : pluralUnit;
        this.countElement.textContent = unit ? `${visibleCount} ${unit}` : String(visibleCount);
      }
    }

    clear(options = {}) {
      if (!this.searchInput) return;
      this.searchInput.value = '';
      this.filter();
      if (options.focus !== false) this.searchInput.focus();
    }

    setActiveGroupLink(activeLink) {
      this.groupLinks.forEach((link) => {
        if (link === activeLink) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    syncActiveGroupLinkFromHash() {
      const activeLink = this.groupLinks.find((link) => link.hash === window.location.hash);
      this.setActiveGroupLink(activeLink || null);
    }

    handleBlockSelect(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !this.contains(target)) return;

      if (this.searchInput && this.searchInput.value) this.clear({ focus: false });

      const item = target.closest('[data-brand-item]');
      if (!item) return;

      item.hidden = false;
      const group = item.closest('[data-brand-group][data-group-key]');
      if (group) {
        group.hidden = false;
        const link = this.groupLinks.find(
          (candidate) => candidate.dataset.groupTarget === group.dataset.groupKey,
        );
        const alphabetItem = link ? link.closest('[data-alphabet-item]') : null;
        if (alphabetItem) alphabetItem.hidden = false;
      }

      item.scrollIntoView({ block: 'nearest' });
    }
  }

  customElements.define(elementName, KiekkoKingiBrandDirectory);
})();
